import { getEnv } from '@/composables/useEnv'
import { apiExportables, tilesetExportables } from '@/config/layers'
import { tilesetIdFromUrl } from '@/utils/layers/dataLayers'
import {
  IBaseDataLayer,
  IMetadataAttribute,
  TExportType,
  TLayerMetadata,
  TMetadataResponse
} from '@lifk/types'
import {
  getOrdinalNumberStr,
  isNumber,
  roundAndTruncate,
  roundToSigDigits
} from '@lifk/utils'
import axios from 'axios'

/*
  Attribute Name/Key: attribute identifier, not for display (eg- 'val' or 'calenviroscreen_percentile')
  Display Name: attribute display name (eg- 'Temperature' or 'CalEnviroscreen')
  Attribute Value: location-specific value of the attribute, unformatted (eg- '90')
  Formatted Value: formatted Attribute Value, usually including units (eg- 90°F or '35th percentile')
*/

export const getMetadataByName = (
  attributeMetadata: IMetadataAttribute[],
  attributeName: string
) => {
  return (
    attributeMetadata.find((attribute) => attribute.name == attributeName) ?? {
      displayName: attributeName,
      name: attributeName,
      dataType: ''
    }
  )
}

export const getFormattedAttrValWithUnits = (
  formattedAttrVal: string,
  unitKey: string | undefined,
  unitStr: string | undefined
): string => {
  if (unitKey && unitStr) {
    switch (unitKey) {
      case 'percent':
      case 'fahrenheit':
        return `${formattedAttrVal}${unitStr}`
      case 'dollars':
        return `$${formattedAttrVal}`
      default:
        return `${formattedAttrVal} ${unitStr}`
    }
  }
  return formattedAttrVal
}

export const formatAttrValue = (
  attrValue: string | number | undefined,
  isPercentile: boolean,
  dataType: string,
  truncate = true
): string => {
  if (isNumber(attrValue)) {
    const attrNumber = Number(attrValue)
    if (isPercentile) {
      return getOrdinalNumberStr(attrNumber) ?? ''
    } else if (
      dataType == 'FLOAT' ||
      dataType == 'DOUBLE' ||
      dataType === 'INTEGER'
    ) {
      // This must be explicitly based on data type, to accommodate numeric values
      //   that should be displayed as strings, such as 'geoid'.

      // 3 sig digits for 3-digit numbers (mainly to accommodate temperature layer)
      const sigDigits = attrNumber > 100 && attrNumber < 1000 ? 3 : 2
      if (truncate) {
        return roundAndTruncate(attrNumber, sigDigits)
      } else {
        return roundToSigDigits(attrNumber, sigDigits).toLocaleString()
      }
    }
  }
  return attrValue ? `${attrValue}` : ''
}

export const requestMetadata = async (dataLayers: IBaseDataLayer[]) => {
  const tilesetIds: Set<string> = new Set()
  const layerValueTilesets: Record<
    string,
    { tilesetId: string; tilesetLayer: string | undefined }
  > = {} // {layerValueId: {...}}

  for (const dataLayer of dataLayers) {
    if (!dataLayer.mapLayers) continue

    for (const { layerValues } of dataLayer.mapLayers) {
      for (const layerValue of layerValues) {
        const tilesetId = tilesetIdFromUrl(layerValue.tilesetUrl, false)
        if (tilesetId) {
          layerValueTilesets[layerValue.key] = {
            tilesetId,
            tilesetLayer: layerValue.tilesetLayer
          }
          tilesetIds.add(tilesetId)
        }
      }
    }
  }

  const newMetadata: TLayerMetadata = {}
  if (tilesetIds.size) {
    const response = (await queryMetadata(
      Array.from(tilesetIds)
    )) as TMetadataResponse

    // parse response into TLayerMetadata
    for (const [layerValueKey, layerValueTilesetData] of Object.entries(
      layerValueTilesets
    )) {
      newMetadata[layerValueKey] = {}
      const layerValueMetadataResp =
        response[layerValueTilesetData.tilesetId] ?? {}

      for (const [exportType, metadata] of Object.entries(
        layerValueMetadataResp
      )) {
        const newAttributes: IMetadataAttribute[] =
          layerValueTilesetData.tilesetLayer &&
          metadata.attributes[layerValueTilesetData.tilesetLayer]
            ? metadata.attributes[layerValueTilesetData.tilesetLayer]
            : Object.entries(metadata.attributes).flatMap(
                ([_, attributeVal]) => attributeVal
              )
        // Some TilesetLayers within a Tileset may have duplicate attributes
        // This assumes that if the name matches, the metadata is exactly the same
        // and can be shared for all TilesetLayer features within the Tileset.
        const uniqueNewAttributes: IMetadataAttribute[] = []
        newAttributes.forEach((attr) => {
          if (uniqueNewAttributes.some(({ name }) => name === attr.name)) return
          uniqueNewAttributes.push(attr)
        })
        newMetadata[layerValueKey][exportType] = uniqueNewAttributes

        // if we're dealing with a vectorized raster tileset,
        // the attribute name on the feature is always 'val'
        if (exportType === 'vectorized-raster-tileset') {
          newMetadata[layerValueKey][exportType]?.forEach(
            (a) => (a.name = 'val')
          )
        }
      }
    }
    return newMetadata
  }
}

const queryMetadata = async (tilesetNames: string[]) => {
  try {
    const baseURL =
      getEnv('VITE_APP_METADATA_API_URL') ??
      'https://field-kit-data-c11a5e538ab6.herokuapp.com'
    const layersQuery = tilesetNames.join(',')
    const version = getEnv('VITE_APP_LAYER_METADATA_VERSION') ?? 'prod'
    const url = `${baseURL}/metadata.json?layers=${layersQuery}&version=${version}`

    const response = await axios.get(url)
    if (response?.data?.layers) {
      return response.data.layers
    }
  } catch (e) {
    console.error('Could not fetch data layer metadata', e)
  }
  return null
}

export const metadataFor = (
  exportableType: 'tileset' | 'api',
  layerValueKey: string,
  metadata: TLayerMetadata | undefined
) => {
  if (!metadata || !metadata[layerValueKey]) return []
  let attributes: IMetadataAttribute[] = []
  Object.entries(metadata[layerValueKey]).forEach(([exportType, attrs]) => {
    const typedExportType = exportType as TExportType
    if (
      (exportableType === 'tileset' &&
        tilesetExportables.includes(typedExportType)) ||
      (exportableType === 'api' && apiExportables.includes(typedExportType))
    ) {
      attributes = attributes.concat(attrs)
    }
  })
  return attributes
}
